/**
 * Scenario definition schema.
 *
 * A scenario is a directed graph of nodes. Facilitators step through it; players vote on
 * `decision` nodes; `branch` nodes route on scenario variables; `inject` nodes are
 * facilitator-triggered curveballs; the graph ends in a `debrief` node.
 *
 * All human-readable strings are authored in English. Translations are stored separately as
 * flattened JSON-path → string maps (see `localize.ts`), so this schema stays plain strings.
 */
import { z } from "zod";

export const OptionQuality = z.enum(["best", "good", "risky", "bad"]);
export type OptionQuality = z.infer<typeof OptionQuality>;

export const Effects = z.object({
  /** Deltas applied to scenario variables, e.g. { attackerTrust: +20 }. */
  vars: z.record(z.string(), z.number()).default({}),
  /** Points earned per scoring dimension, e.g. { verify: 3, contain: 1 }. */
  score: z.record(z.string(), z.number()).default({}),
});
export type Effects = z.infer<typeof Effects>;

export const DecisionOption = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  detail: z.string().optional(),
  quality: OptionQuality,
  /** Shown to everyone when the decision is revealed. */
  feedback: z.string().min(1),
  effects: Effects.default({ vars: {}, score: {} }),
  /** Next node when this option wins the vote. */
  next: z.string().min(1),
  /** Roles whose job this option most directly is; they get a small bonus for choosing it. */
  roleBonus: z.array(z.string()).default([]),
  /** Free-form tags used by badges (e.g. "verify", "escalate", "slow-down"). */
  tags: z.array(z.string()).default([]),
});
export type DecisionOption = z.infer<typeof DecisionOption>;

export const TranscriptLine = z.object({
  speaker: z.enum(["caller", "callee", "system"]),
  /** Display name override for the speaker (defaults to caller/callee names). */
  name: z.string().optional(),
  text: z.string().min(1),
  /** A "tell" — the manipulation technique hiding in this line, shown at the reveal. */
  tell: z.string().optional(),
  /** Seconds to hold before the next line appears on the big screen. */
  pauseMs: z.number().int().min(0).max(20000).optional(),
});
export type TranscriptLine = z.infer<typeof TranscriptLine>;

const baseNode = {
  title: z.string().min(1),
  /** Private notes shown only to players holding a role: roleId → text. */
  roleNotes: z.record(z.string(), z.string()).default({}),
  /** Facilitator-only guidance for this node. */
  facilitatorNotes: z.string().optional(),
};

export const NarrativeNode = z.object({
  ...baseNode,
  type: z.literal("narrative"),
  body: z.string().min(1),
  /** Optional artefact rendered as a phone/email/SMS/chat/ticket card. */
  artifact: z
    .object({
      kind: z.enum(["email", "sms", "chat", "ticket", "news", "memo", "alert"]),
      from: z.string().optional(),
      to: z.string().optional(),
      subject: z.string().optional(),
      body: z.string().min(1),
      timestamp: z.string().optional(),
    })
    .optional(),
  next: z.string().nullable(),
});

export const CallNode = z.object({
  ...baseNode,
  type: z.literal("call"),
  caller: z.object({
    name: z.string().min(1),
    /** What the phone display shows — often spoofed. */
    displayNumber: z.string().min(1),
    spoofed: z.boolean().default(false),
    /** Voice profile hint for the reveal (e.g. "synthetic clone of the CFO"). */
    voice: z.string().optional(),
  }),
  callee: z.object({ name: z.string().min(1), roleId: z.string().optional() }),
  transcript: z.array(TranscriptLine).min(1),
  /** Red flags in this call, revealed at the debrief. */
  redFlags: z.array(z.string()).default([]),
  next: z.string().nullable(),
});

export const DecisionNode = z.object({
  ...baseNode,
  type: z.literal("decision"),
  prompt: z.string().min(1),
  context: z.string().optional(),
  /** Default voting window in seconds; the facilitator can extend it. */
  timerSec: z.number().int().min(10).max(900).default(90),
  /** Who may vote: everyone with a player seat, or only listed roles. */
  voters: z.union([z.literal("all"), z.array(z.string()).min(1)]).default("all"),
  options: z.array(DecisionOption).min(2).max(6),
});

export const PollNode = z.object({
  ...baseNode,
  type: z.literal("poll"),
  prompt: z.string().min(1),
  options: z
    .array(z.object({ id: z.string().min(1), label: z.string().min(1) }))
    .min(2)
    .max(8),
  timerSec: z.number().int().min(10).max(900).default(60),
  next: z.string().nullable(),
});

export const RevealNode = z.object({
  ...baseNode,
  type: z.literal("reveal"),
  body: z.string().min(1),
  lessons: z.array(z.string()).default([]),
  next: z.string().nullable(),
});

export const BranchNode = z.object({
  ...baseNode,
  type: z.literal("branch"),
  conditions: z
    .array(
      z.object({
        var: z.string().min(1),
        op: z.enum([">", ">=", "<", "<=", "==", "!="]),
        value: z.number(),
        next: z.string().min(1),
      }),
    )
    .min(1),
  else: z.string().min(1),
});

export const DebriefNode = z.object({
  ...baseNode,
  type: z.literal("debrief"),
  body: z.string().min(1),
});

export const ScenarioNode = z.discriminatedUnion("type", [
  NarrativeNode,
  CallNode,
  DecisionNode,
  PollNode,
  RevealNode,
  BranchNode,
  DebriefNode,
]);
export type ScenarioNode = z.infer<typeof ScenarioNode>;
export type NodeType = ScenarioNode["type"];
export type NarrativeNode = z.infer<typeof NarrativeNode>;
export type CallNode = z.infer<typeof CallNode>;
export type DecisionNode = z.infer<typeof DecisionNode>;
export type PollNode = z.infer<typeof PollNode>;
export type RevealNode = z.infer<typeof RevealNode>;
export type BranchNode = z.infer<typeof BranchNode>;
export type DebriefNode = z.infer<typeof DebriefNode>;

export const Inject = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  /** Node id in `nodes` to jump to when triggered; that node's `next` chain must rejoin the graph. */
  node: z.string().min(1),
  /** Only offer this inject after one of these nodes has been visited (empty = always). */
  availableAfter: z.array(z.string()).default([]),
});
export type Inject = z.infer<typeof Inject>;

export const Role = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** Lucide icon name (client maps it). */
  icon: z.string().default("user"),
  brief: z.string().min(1),
  objectives: z.array(z.string()).default([]),
  maxPlayers: z.number().int().min(1).optional(),
});
export type Role = z.infer<typeof Role>;

export const Dimension = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  description: z.string().min(1),
});
export type Dimension = z.infer<typeof Dimension>;

export const BadgeCondition = z.discriminatedUnion("kind", [
  /** Voted for options carrying `tag` at least `min` times. */
  z.object({ kind: z.literal("optionTag"), tag: z.string(), min: z.number().int().min(1) }),
  /** Average time-to-vote under `fraction` of the timer (0..1). */
  z.object({
    kind: z.literal("speed"),
    fraction: z.number().min(0).max(1),
    minVotes: z.number().int().min(1),
  }),
  /** Voted with the winning option at least `fraction` of the time. */
  z.object({
    kind: z.literal("consensus"),
    fraction: z.number().min(0).max(1),
    minVotes: z.number().int().min(1),
  }),
  /** Picked `best` on at least `min` decisions. */
  z.object({ kind: z.literal("bestPicks"), min: z.number().int().min(1) }),
  /** Team-level: a variable stayed below `below` all game. */
  z.object({ kind: z.literal("teamVarBelow"), var: z.string(), below: z.number() }),
  /** Team-level: final dimension score ≥ `min` percent. */
  z.object({ kind: z.literal("teamDimension"), dimension: z.string(), min: z.number().min(0).max(100) }),
]);
export type BadgeCondition = z.infer<typeof BadgeCondition>;

export const Badge = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  icon: z.string().default("award"),
  condition: BadgeCondition,
});
export type Badge = z.infer<typeof Badge>;

/**
 * Who the exercise is written for. The mechanics are identical across tracks — a call, a decision,
 * a reveal — but the roles, the vocabulary, the pacing and the report's framing differ, and a
 * facilitator running a library evening should not have to read past business-email compromise to
 * find the scam call their audience actually gets.
 */
export const Audience = z.enum(["workplace", "community", "youth"]);
export type Audience = z.infer<typeof Audience>;

export const Scenario = z.object({
  id: z
    .string()
    .min(3)
    .regex(/^[a-z0-9-]+$/, "scenario ids are lowercase kebab-case"),
  version: z.string().min(1),
  title: z.string().min(1),
  tagline: z.string().min(1),
  summary: z.string().min(1),
  difficulty: z.enum(["intro", "standard", "advanced"]),
  estimatedMinutes: z.number().int().min(10).max(240),
  audience: Audience.default("workplace"),
  /** Who exactly, in the facilitator's words: "Ages 12–16", "Seniors and the people who help them". */
  audienceNote: z.string().optional(),
  /** Real incidents this scenario is modelled on. */
  basedOn: z
    .array(z.object({ name: z.string(), year: z.number().int(), url: z.string().url().optional() }))
    .default([]),
  tags: z.array(z.string()).default([]),
  learningObjectives: z.array(z.string()).min(1),
  dimensions: z.array(Dimension).min(2).max(8),
  variables: z.record(z.string(), z.number()).default({}),
  roles: z.array(Role).min(2),
  start: z.string().min(1),
  nodes: z.record(z.string(), ScenarioNode),
  injects: z.array(Inject).default([]),
  badges: z.array(Badge).default([]),
});
export type Scenario = z.infer<typeof Scenario>;
export type ScenarioInput = z.input<typeof Scenario>;

/** Public, lightweight catalogue entry. */
export interface ScenarioSummary {
  id: string;
  version: string;
  title: string;
  tagline: string;
  summary: string;
  difficulty: Scenario["difficulty"];
  estimatedMinutes: number;
  audience: Audience;
  audienceNote?: string;
  tags: string[];
  roles: { id: string; name: string; icon: string }[];
  decisions: number;
  injects: number;
  basedOn: Scenario["basedOn"];
  learningObjectives: string[];
  /** Locales with a translation file, and how complete each is (0..1). */
  translations: Record<string, number>;
}

export function parseScenario(input: unknown): Scenario {
  return Scenario.parse(input);
}
